import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import { fetchStreams, fetchStreamStats } from '../../../store/actions/StreamActions';
import { selectMaxStreams } from '../../../store/selectors/SubscriptionSelectors';
import { selectIsAdmin } from '../../../store/selectors/AuthSelectors';
import useWebSocket from '../../../hooks/useWebSocket';
import StreamService from '../../../services/StreamService';
import StreamUnavailable from './StreamUnavailable';
import {
  PageHeader, Card, Button, StatTile, Badge, Dot, EmptyState,
} from '../../ui';

const STREAM_MODE_KEY = 'stream_mode';
const STREAM_MODE_EVENT = 'stream-mode-changed';

const getStoredStreamMode = () =>
  localStorage.getItem(STREAM_MODE_KEY) === 'playlist' ? 'playlist' : 'live';

const formatBytes = (bytes, dash = false) => {
  if (bytes === undefined || bytes === null) return dash ? '—' : '0 MB';
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(2)} GB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const statusTone = (status) => {
  switch (status) {
    case 'broadcasting':
    case 'live': return 'live';
    case 'failover': return 'blue';
    case 'active': return 'brand';
    case 'disconnected': return 'warn';
    default: return 'neutral';
  }
};
const statusLabel = (status) => (status === 'failover' ? 'Failover' : status);

const StreamDashboard = ({ streams, stats, loading, fetchStreams, fetchStreamStats, maxStreams, isAdmin }) => {
  const navigate = useNavigate();
  const [streamMode, setStreamMode] = useState(getStoredStreamMode());
  const [modeSyncing, setModeSyncing] = useState(false);
  const [showGuide, setShowGuide] = useState(() => localStorage.getItem('sd_hide_guide') !== '1');

  const videoRefs = useRef({});
  const hlsInstances = useRef({});
  const [playerStatus, setPlayerStatus] = useState({}); // { [stream.id]: 'loading'|'live'|'unavailable' }
  const { isConnected, streamStats, streamUpdates } = useWebSocket();

  useEffect(() => {
    const onModeChange = (e) => {
      const mode = e?.detail?.mode;
      setStreamMode(mode === 'live' || mode === 'playlist' ? mode : getStoredStreamMode());
    };
    window.addEventListener(STREAM_MODE_EVENT, onModeChange);
    window.addEventListener('storage', onModeChange);
    return () => {
      window.removeEventListener(STREAM_MODE_EVENT, onModeChange);
      window.removeEventListener('storage', onModeChange);
    };
  }, []);

  useEffect(() => {
    (async () => {
      setModeSyncing(true);
      try {
        const res = await StreamService.getStreamMode();
        const mode = res?.mode === 'playlist' ? 'playlist' : 'live';
        setStreamMode(mode);
        localStorage.setItem(STREAM_MODE_KEY, mode);
        window.dispatchEvent(new CustomEvent(STREAM_MODE_EVENT, { detail: { mode } }));
      } catch {
        setStreamMode(getStoredStreamMode());
      } finally {
        setModeSyncing(false);
      }
    })();
  }, []);

  useEffect(() => {
    fetchStreams();
    fetchStreamStats();
    const id = setInterval(() => { fetchStreams(); fetchStreamStats(); }, 30000);
    return () => clearInterval(id);
  }, [fetchStreams, fetchStreamStats]);

  const streamCount = streams ? streams.length : 0;
  const isAtLimit = !isAdmin && maxStreams > 0 && streamCount >= maxStreams;

  const handleNewStream = () => {
    if (streamMode === 'playlist') {
      swal({ title: 'Playlist Mode Active', text: 'Switch to Live mode from the top toggle to create RTMP streams.', icon: 'info', button: 'OK' });
      return;
    }
    if (isAtLimit) {
      swal({
        title: 'Stream Limit Reached',
        text: `Your plan allows ${maxStreams} stream${maxStreams !== 1 ? 's' : ''}. Upgrade to create more.`,
        icon: 'warning',
        buttons: { cancel: 'Cancel', upgrade: { text: 'Upgrade Plan', value: 'upgrade' } },
      }).then((v) => v === 'upgrade' && navigate('/pricing'));
    } else {
      navigate('/create-stream');
    }
  };

  const normalizeRealtime = (stream, wsUpdate) => {
    const viewers = wsUpdate?.viewer_count ?? wsUpdate?.live_count ?? wsUpdate?.clients ?? stream?.viewer_count ?? 0;
    const isLiveValue = wsUpdate?.isLive ?? wsUpdate?.is_live ?? wsUpdate?.active ?? stream?.is_live ?? false;
    const statusValue = wsUpdate?.status || stream?.status || (isLiveValue ? 'broadcasting' : 'inactive');
    return { ...stream, viewer_count: Number(viewers) || 0, is_live: Boolean(isLiveValue) || Number(viewers) > 0, status: statusValue };
  };
  const withRealtime = (stream) => normalizeRealtime(stream, streamUpdates[stream.stream_key]);

  const streamsRT = (streams || []).map(withRealtime);
  const activeStreams = streamsRT.filter((s) => s.is_live || s.status === 'broadcasting' || s.status === 'live' || s.status === 'failover');
  const liveNowCount = streamsRT.filter((s) => (s.is_live || s.status === 'broadcasting' || s.status === 'live') && s.status !== 'failover').length;
  const recentStreams = streamsRT.slice(0, 6);

  useEffect(() => {
    const loadHls = async () => {
      const { attachHls } = await import('../../../utils/hlsPlayer');
      activeStreams.forEach(async (stream) => {
        const video = videoRefs.current[stream.id];
        if (!video || hlsInstances.current[stream.id] || !stream.hls_url) return;
        hlsInstances.current[stream.id] = await attachHls(video, stream.hls_url, {
          onStatusChange: (status) => setPlayerStatus((prev) => ({ ...prev, [stream.id]: status })),
        });
      });
    };
    if (activeStreams.length > 0) loadHls();
    return () => {
      Object.values(hlsInstances.current).forEach((c) => { try { typeof c === 'function' ? c() : c?.destroy?.(); } catch (_) {} });
      hlsInstances.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const liveViewers = streamStats?.total_hls_viewers !== undefined
    ? streamStats.total_hls_viewers
    : (stats?.total_viewers || 0);
  const ingestRate = stats?.nginx_stats?.total_bandwidth_in
    ? `${(stats.nginx_stats.total_bandwidth_in / 1024).toFixed(0)} kbps` : '0 kbps';

  const kpis = [
    { icon: 'fa-video', tone: 'brand', value: stats?.total_streams || 0, label: 'Total Streams' },
    { icon: 'fa-tower-broadcast', tone: 'live', value: liveNowCount, label: 'Live Now' },
    { icon: 'fa-users', tone: 'info', value: liveViewers, label: 'Live Viewers' },
    { icon: 'fa-wifi', tone: 'warn', value: formatBytes(stats?.monthly_bandwidth), label: 'Bandwidth (month)' },
    { icon: 'fa-hard-drive', tone: 'neutral', value: formatBytes(stats?.storage_used, true), label: stats?.storage_total ? `Storage / ${(stats.storage_total / 1073741824).toFixed(1)} GB` : 'Storage Used' },
    { icon: 'fa-gauge-high', tone: 'brand', value: ingestRate, label: 'Current Ingest' },
  ];

  const dismissGuide = () => { setShowGuide(false); localStorage.setItem('sd_hide_guide', '1'); };

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Manage your streams, viewers and automation"
        actions={
          <>
            <Badge tone={streamMode === 'playlist' ? 'info' : 'good'}>
              <i className={`fa-solid ${streamMode === 'playlist' ? 'fa-list' : 'fa-tower-broadcast'}`} />
              {streamMode === 'playlist' ? 'Playlist mode' : 'Live mode'}
            </Badge>
            <Button variant="secondary" icon="fa-rotate" onClick={() => { fetchStreams(); fetchStreamStats(); }} disabled={loading}>
              Refresh
            </Button>
            <Button
              variant={isAtLimit ? 'outline' : 'primary'}
              icon={isAtLimit ? 'fa-arrow-up' : 'fa-plus'}
              onClick={handleNewStream}
              disabled={streamMode === 'playlist'}
            >
              {isAtLimit ? 'Upgrade Plan' : 'New Stream'}
            </Button>
          </>
        }
      />

      {streamMode === 'playlist' && (
        <div className="tw-mb-6 tw-rounded-xl tw-border tw-border-info/30 tw-bg-info/10 tw-px-4 tw-py-3 tw-text-sm tw-text-info">
          <strong>Playlist mode is active.</strong> Live stream creation is disabled — scheduled VOD playlists are used for broadcasting.
          {modeSyncing && <span className="tw-ml-2 tw-opacity-70">Applying…</span>}
        </div>
      )}

      <div className="tw-mb-6 tw-grid tw-grid-cols-2 tw-gap-4 sm:tw-grid-cols-3 xl:tw-grid-cols-6">
        {kpis.map((k) => <StatTile key={k.label} {...k} />)}
      </div>

      <div className="tw-grid tw-gap-6 lg:tw-grid-cols-3">
        <div className="tw-space-y-6 lg:tw-col-span-2">
          <Card
            title={<span className="tw-flex tw-items-center tw-gap-2"><Dot tone="live" pulse /> Live Streams ({activeStreams.length})</span>}
            actions={
              <div className="tw-flex tw-items-center tw-gap-2">
                {isConnected && <Badge tone="good">Real-time</Badge>}
                <Button variant="ghost" size="sm" onClick={() => navigate('/streams')}>View all</Button>
              </div>
            }
          >
            {activeStreams.length === 0 ? (
              <EmptyState
                icon="fa-video-slash"
                title="No streams are live right now"
                hint="Start broadcasting from OBS, or create a new stream to get an RTMP URL."
                action={<Button icon="fa-plus" onClick={handleNewStream}>Create Stream</Button>}
              />
            ) : (
              <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
                {activeStreams.map((stream) => {
                  const wsu = streamUpdates[stream.stream_key];
                  const viewers = wsu?.viewer_count ?? wsu?.live_count ?? wsu?.clients ?? stream.viewer_count ?? 0;
                  return (
                    <div key={stream.id} className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-line tw-bg-ink-850">
                      <div className="tw-relative tw-bg-black">
                        {playerStatus[stream.id] === 'unavailable' ? (
                          <StreamUnavailable compact />
                        ) : (
                          <video
                            ref={(el) => { videoRefs.current[stream.id] = el; }}
                            className="tw-h-44 tw-w-full tw-object-cover"
                            controls muted playsInline
                          />
                        )}
                        <span className="tw-absolute tw-left-2 tw-top-2 tw-inline-flex tw-items-center tw-gap-1 tw-rounded tw-bg-live tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-bold tw-text-white">
                          <Dot tone="neutral" /> LIVE
                        </span>
                        <span className="tw-absolute tw-right-2 tw-top-2 tw-rounded tw-bg-black/70 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-heading">
                          <i className="fa-solid fa-eye tw-mr-1" />{viewers}
                        </span>
                      </div>
                      <div className="tw-p-3">
                        <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                          <div className="tw-min-w-0">
                            <h4 className="tw-truncate tw-font-semibold tw-text-heading">{stream.name}</h4>
                            <p className="tw-truncate tw-text-xs tw-text-content-faint">Key: {stream.stream_key.substring(0, 8)}…</p>
                          </div>
                          <Badge tone={statusTone(stream.status)}>{statusLabel(stream.status)}</Badge>
                        </div>
                        {wsu?.bandwidth_in > 0 && (
                          <p className="tw-mt-2 tw-text-xs tw-text-good">
                            <i className="fa-solid fa-signal tw-mr-1" />{(wsu.bandwidth_in / 1024).toFixed(0)} kbps in
                          </p>
                        )}
                        <Button variant="secondary" size="sm" className="tw-mt-3 tw-w-full" onClick={() => navigate(`/streams/${stream.id}`)}>
                          View Details
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {showGuide && (
            <Card
              title="Quick Start"
              actions={<Button variant="ghost" size="sm" icon="fa-xmark" onClick={dismissGuide} />}
            >
              <div className="tw-grid tw-gap-4 sm:tw-grid-cols-3">
                {[
                  ['1', 'Create a stream', 'Get a unique RTMP URL and stream key'],
                  ['2', 'Configure OBS', 'Paste the URL + key into OBS Studio settings'],
                  ['3', 'Go live', 'Share the HLS/DASH URL — viewers watch anywhere'],
                ].map(([n, t, d]) => (
                  <div key={n} className="tw-flex tw-gap-3">
                    <span className="tw-flex tw-h-7 tw-w-7 tw-flex-none tw-items-center tw-justify-center tw-rounded-full tw-bg-brand-soft tw-text-sm tw-font-bold tw-text-brand">{n}</span>
                    <div>
                      <h4 className="tw-text-sm tw-font-semibold tw-text-heading">{t}</h4>
                      <p className="tw-text-xs tw-text-content-muted">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div className="tw-space-y-6">
          <Card title="Automation">
            <div className="tw-space-y-0 tw-text-sm">
              {[
                ['Broadcast mode', <Badge tone={streamMode === 'playlist' ? 'info' : 'good'}>{streamMode === 'playlist' ? 'Playlist' : 'Live'}</Badge>],
                ['Auto-failover to VOD', <Badge tone="good">On</Badge>],
                ['Stream slots used', <span className="tw-font-bold tw-text-heading">{streamCount}{!isAdmin && maxStreams > 0 ? ` / ${maxStreams}` : ''}</span>],
              ].map(([label, node], i) => (
                <div key={i} className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-line tw-py-2.5 last:tw-border-0">
                  <span className="tw-text-content-muted">{label}</span>
                  {node}
                </div>
              ))}
            </div>
            <div className="tw-mt-4 tw-space-y-2">
              <Button variant="outline" size="sm" icon="fa-calendar-days" className="tw-w-full" onClick={() => navigate('/vod/schedules')}>
                Schedules &amp; Failover
              </Button>
              <Button variant="secondary" size="sm" icon="fa-list" className="tw-w-full" onClick={() => navigate('/vod/playlists')}>
                Manage Playlists
              </Button>
            </div>
          </Card>

          <Card title="Server Status">
            <div className="tw-space-y-2.5 tw-text-sm">
              {[['Nginx RTMP', 'Running'], ['HLS / DASH', 'Active'], ['API Server', 'Online'], ['Database', 'Connected']].map(([l, s]) => (
                <div key={l} className="tw-flex tw-items-center tw-justify-between">
                  <span className="tw-text-content-muted">{l}</span>
                  <Badge tone="good"><Dot tone="good" /> {s}</Badge>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Recent Streams" actions={<Button variant="ghost" size="sm" onClick={() => navigate('/streams')}>All</Button>}>
            {recentStreams.length === 0 ? (
              <p className="tw-py-4 tw-text-center tw-text-sm tw-text-content-muted">No streams created yet</p>
            ) : (
              <div className="tw--my-2 tw-divide-y tw-divide-line">
                {recentStreams.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => navigate(`/streams/${s.id}`)}
                    className="tw-flex tw-w-full tw-items-center tw-justify-between tw-py-2.5 tw-text-left hover:tw-opacity-80"
                  >
                    <div className="tw-min-w-0">
                      <div className="tw-truncate tw-text-sm tw-font-medium tw-text-heading">{s.name}</div>
                      <div className="tw-text-xs tw-text-content-faint">{new Date(s.created_at).toLocaleDateString()}</div>
                    </div>
                    <Badge tone={statusTone(s.status)}>{statusLabel(s.status)}</Badge>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  streams: state.streams.streams,
  stats: state.streams.stats,
  loading: state.streams.loading,
  maxStreams: selectMaxStreams(state),
  isAdmin: selectIsAdmin(state),
});

export default connect(mapStateToProps, { fetchStreams, fetchStreamStats })(StreamDashboard);
